// Ekran 06 — KPI şeridi ve inceleme panelinin ortak türetmeleri.
//
// Buradaki hiçbir fonksiyon EŞİK bilmez: bantlar sunucudan (`ayar_esik`) geliyor.
// Sayıların kendisi de sunucudan gelir; burası yalnız toplama/ortalama alır —
// aynı türetme iki bileşende ayrı yazılırsa KPI ile panel birbirinden ayrışır.

import type { BeyanGunuDto, BeyanSatiri, TesisBeyanTipi, TesisSatiri } from './api'

/** TESİS bloğunun bir satırı (üretim · iç ihtiyaç · satış · gaz tüketimi · taahhüt). */
export function tesisSatiri(gun: BeyanGunuDto, tip: TesisBeyanTipi): TesisSatiri | undefined {
  return gun.tesis.find((t) => t.tip === tip)
}

export function tesisToplam(gun: BeyanGunuDto, tip: TesisBeyanTipi): number | null {
  return tesisSatiri(gun, tip)?.toplam ?? null
}

/**
 * Saatlik dizinin toplamı. Hiç dolu hücre yoksa `null` döner — sıfır yazmak
 * "beyan sıfırdı" demek olurdu (K32: boş alan boş kalır).
 */
export function toplaBos(dizi: (number | null)[]): number | null {
  let t: number | null = null
  for (const d of dizi) if (d !== null) t = (t ?? 0) + d
  return t
}

/** Satırın DOLU hücre değerleri — ortalama/maksimum bunlar üzerinden alınır. */
export function doluDegerler(satir: BeyanSatiri | undefined): number[] {
  if (!satir) return []
  const cikti: number[] = []
  for (const h of satir.hucreler) if (h && h.deger !== null) cikti.push(h.deger)
  return cikti
}

export function ortalama(satir: BeyanSatiri | undefined): number | null {
  const v = doluDegerler(satir)
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null
}

export function enBuyuk(satir: BeyanSatiri | undefined): number | null {
  const v = doluDegerler(satir)
  return v.length ? Math.max(...v) : null
}

/** Gaz analiz ünitesinin (GA-*) bir parametre satırı — CH₄ · CO₂ · H₂S · O₂. */
export function gazSatiri(gun: BeyanGunuDto, ad: string): BeyanSatiri | undefined {
  return gun.uniteler.find((u) => u.kod.startsWith('GA'))?.satirlar.find((s) => s.ad === ad)
}

/**
 * Satırın herhangi bir hücresi eşik dışı mı? Bandı sunucu veriyor; burada sabit
 * bir sınır (ör. 300 ppm) yazılsaydı Ayarlar'dan eşik değişince uyarı
 * sessizce yanlışlanırdı (§8-14).
 */
export function esikDisi(satir: BeyanSatiri | undefined): boolean {
  return (satir?.hucreler ?? []).some((h) => h?.bant != null && h.bant !== 'saglikli')
}

/** Gün boyunca görülen EN KÖTÜ bant — KPI kartının rengi tek bir hükme iner. */
export function enKotuBant(bantlar: (string | null)[]): 'kritik' | 'izle' | null {
  if (bantlar.includes('kritik')) return 'kritik'
  if (bantlar.includes('izle')) return 'izle'
  return null
}

/** Olay tipine göre gün kayıtları; ünitesi olmayan kayıt her satırda görünür. */
export function olayKayitlari(gun: BeyanGunuDto, satir: BeyanSatiri, uniteId: string) {
  if (satir.olayTipi === null) return []
  return gun.olaylar.filter(
    (o) => o.tip === satir.olayTipi && (o.uniteId === null || o.uniteId === uniteId),
  )
}
